import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Bot, MessageSquare, ExternalLink, Loader2, Send, X, User } from 'lucide-react'

interface Agent {
  id: string
  name: string
  url: string
  webuiUrl: string
  port: number
  status: 'active' | 'inactive'
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export default function Dashboard() {
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [chatOpen, setChatOpen] = useState(false)
  const [webuiOpen, setWebuiOpen] = useState(false)
  const [currentAgent, setCurrentAgent] = useState<Agent | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadAgents()
  }, [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const loadAgents = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch('/api/agents', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await res.json()
      setAgents(data.agents || [])
    } catch {
      console.error('Failed to load agents')
    } finally {
      setLoading(false)
    }
  }

  const openWebUI = (agent: Agent) => {
    setCurrentAgent(agent)
    setWebuiOpen(true)
  }

  const openChat = (agent: Agent) => {
    setCurrentAgent(agent)
    setChatOpen(true)
    setMessages([{ role: 'assistant', content: "Hello! I'm ready to help you. What would you like to know?" }])
  }

  const sendMessage = async () => {
    if (!input.trim() || !currentAgent) return

    const userMsg = input.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', content: userMsg }])
    setSending(true)

    try {
      // Send through backend (which forwards to agent + Telegram)
      const token = localStorage.getItem('token')
      const res = await fetch(`/api/agents/${currentAgent.id}/chat`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: userMsg })
      })

      const data = await res.json()
      setMessages(prev => [...prev, { role: 'assistant', content: data.response || 'Sorry, I could not process that.' }])
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: Could not connect to agent.' }])
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    )
  }

  return (
    <div className="py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Dashboard</h1>
            <p className="text-text-muted">Manage your AI agents</p>
          </div>
          <Link 
            to="/onboarding" 
            className="px-5 py-2 bg-primary text-dark font-semibold rounded-xl hover:opacity-90 transition text-sm"
          >
            + New Agent
          </Link>
        </div>

        {agents.length === 0 ? (
          <div className="text-center py-20 rounded-2xl bg-glass border border-glass-border">
            <Bot className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No agents yet</h3>
            <p className="text-text-muted mb-6">Deploy your first AI agent to get started</p>
            <Link to="/onboarding" className="px-6 py-3 bg-primary text-dark font-bold rounded-xl hover:opacity-90 transition">
              Deploy Agent
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {agents.map(agent => (
              <div key={agent.id} className="p-6 rounded-2xl bg-glass border border-glass-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Bot className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{agent.name || `Agent ${agent.id.slice(-8)}`}</h3>
                    <p className="text-sm text-text-muted">Port: {agent.port}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-success/10 text-success text-xs font-semibold rounded-full border border-success/30">
                    ● Active
                  </span>
                  <button 
                    onClick={() => openChat(agent)}
                    className="px-4 py-2 bg-primary text-dark text-sm font-semibold rounded-lg hover:opacity-90 transition flex items-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Chat
                  </button>
                  <button 
                    onClick={() => openWebUI(agent)}
                    className="px-4 py-2 bg-secondary text-white text-sm font-semibold rounded-lg hover:opacity-90 transition flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open WebUI
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Open WebUI Modal */}
      {webuiOpen && currentAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-6xl h-[90vh] bg-dark border border-glass-border rounded-2xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-glass-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ExternalLink className="w-6 h-6 text-secondary" />
                <div>
                  <h3 className="font-semibold text-white">Open WebUI - {currentAgent.name || 'Agent'}</h3>
                  <p className="text-xs text-text-muted">Powered by Ollama</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a 
                  href={currentAgent.webuiUrl} 
                  target="_blank"
                  className="px-3 py-1 text-xs bg-glass border border-glass-border rounded-lg text-text-muted hover:text-white transition"
                >
                  Open in New Tab
                </a>
                <button 
                  onClick={() => setWebuiOpen(false)}
                  className="p-2 hover:bg-glass rounded-lg transition"
                >
                  <X className="w-5 h-5 text-text-muted" />
                </button>
              </div>
            </div>
            
            {/* iframe */}
            <div className="flex-1 overflow-hidden">
              <iframe 
                src={currentAgent.webuiUrl}
                className="w-full h-full border-0"
                title="Open WebUI"
                allow="clipboard-write; fullscreen"
              />
            </div>
          </div>
        </div>
      )}
      {chatOpen && currentAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-2xl h-[600px] bg-dark border border-glass-border rounded-2xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-glass-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bot className="w-6 h-6 text-primary" />
                <div>
                  <h3 className="font-semibold text-white">{currentAgent.name || 'Agent'}</h3>
                  <p className="text-xs text-text-muted">Online</p>
                </div>
              </div>
              <button 
                onClick={() => setChatOpen(false)}
                className="p-2 hover:bg-glass rounded-lg transition"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                  )}
                  <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                    msg.role === 'user' 
                      ? 'bg-primary text-dark' 
                      : 'bg-glass border border-glass-border text-text'
                  }`}>
                    {msg.content}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-secondary" />
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                  <div className="p-3 rounded-2xl bg-glass border border-glass-border">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input */}
            <div className="px-4 py-3 border-t border-glass-border flex gap-3">
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && sendMessage()}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 bg-darker border border-glass-border rounded-xl text-white text-sm focus:border-primary focus:outline-none transition"
              />
              <button
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                className="px-4 py-2 bg-primary text-dark rounded-xl hover:opacity-90 transition disabled:opacity-30"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
